import { memo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ThemedCurlyBracesIcon } from "assets/CXFIcons/Designer/ContextActions";
import SoftBox from "components/SoftBox";
import CxfMonacoEditor from "components/CxfMonacoEditor";
import CustomDrawerPanel from "layouts/Flow Designer/components/CustomDrawerPanel";
import { stringifyWithSpaces } from "helpers/common";
import { EditorExpansion } from "../EditorExpansion";
import ContextHeaderActions from "./ContextHeaderActions";
import { STEP_STATUSES, CONTEXT_FILTERS } from "constants/flowDesigner";
import { THEME_TYPES, THEME_COLORS } from "constants/theme";
import { hideComponentData } from "helpers/common";

const ContextPanel = () => {
   const { showContextPanel, context, contextFilterOptions } = useSelector((store) => store?.designer?.editorContext);
   const currentFlowName = useSelector((store) => store?.designer?.flows?.currentSavedFlow?.name);
   const { t } = useTranslation("flowDesigner");
   const [editorExpanded, setEditorExpanded] = useState(false);
   const [contextToDisplay, setContextToDisplay] = useState({});
   const editorRef = useRef(null);
   const isDark = useSelector((store) => store?.portal?.settings.themeMode) === THEME_TYPES.DARK;

   const handleEditorDidMount = (editor) => {
      editorRef.current = editor;
   };

   const generateContextToDisplay = () => {
      let contextCopy = JSON.parse(JSON.stringify(context));

      if (currentFlowName) {
      contextCopy.flow_name = currentFlowName;
   } else {
      console.warn("ContextPanel: currentFlowName is missing from Redux", currentFlowName);
   }

      if (contextCopy?.credentialsManagement) {
         contextCopy = hideSensitiveData(contextCopy);
      }

      contextCopy = adjustContextBasedOnFilterOptions(contextCopy);
      contextCopy = hideComponentData(contextCopy);

      setContextToDisplay(contextCopy);
   };

const hideSensitiveData = (contextCopy) => {
   const maskObject = (obj) => {
      if (!obj || typeof obj !== "object") return;

      Object.keys(obj).forEach((key) => {
         const keyLC = key.toLowerCase();
   
         const sensitiveKeywords = ["password", "token", "secret", "key", "auth"];

         if (sensitiveKeywords.some(keyword => keyLC.includes(keyword))) {
            obj[key] = "************";
         } else if (typeof obj[key] === "object") {
            maskObject(obj[key]);
         }
      });
   };
   if (contextCopy.credentialsManagement) {
      Object.values(contextCopy.credentialsManagement).forEach((credential) => {
         maskObject(credential);
      });
   }

   if (contextCopy.session) {
      maskObject(contextCopy.session);
   }

   return contextCopy;
};

   const adjustContextBasedOnFilterOptions = (contextCopy) => {
      let steps = contextCopy.steps;

      if (!contextFilterOptions.componentSteps) {
         steps = hideStepsByOption(steps, CONTEXT_FILTERS.COMPONENT_STEPS);
      }

      if (!contextFilterOptions.connectorSteps) {
         steps = hideStepsByOption(steps, CONTEXT_FILTERS.CONTEXT_FILTERS);
      }

      if (!contextFilterOptions.successfulSteps) {
         steps = hideStepsByOption(steps, CONTEXT_FILTERS.SUCCESSFUL_STEPS);
      }

      if (!contextFilterOptions.failedSteps) {
         steps = hideStepsByOption(steps, CONTEXT_FILTERS.FAILED_STEPS);
      }

      contextCopy.steps = steps;

      return contextCopy;
   };

   const hideStepsByOption = (steps, option) => {
      let filteredSteps = {};

      Object.keys(steps).forEach((key) => {
         const step = steps[key];

         switch (option) {
            case CONTEXT_FILTERS.COMPONENT_STEPS:
               if (step.type !== "component") filteredSteps[key] = step;
               break;
            case CONTEXT_FILTERS.CONTEXT_FILTERS:
               if (step.type !== "connector") filteredSteps[key] = step;
               break;
            case CONTEXT_FILTERS.SUCCESSFUL_STEPS:
               if (step.status !== STEP_STATUSES.SUCCESS) filteredSteps[key] = step;
               break;
            case CONTEXT_FILTERS.FAILED_STEPS:
               if (step.status !== STEP_STATUSES.FAILED) filteredSteps[key] = step;
               break;
            default:
               filteredSteps[key] = step;
         }
      });

      return filteredSteps;
   };

   useEffect(() => {
      generateContextToDisplay();
   }, [context, contextFilterOptions,currentFlowName]);

   // Disable scroll when fullscreen
   useEffect(() => {
      document.body.style.overflow = editorExpanded ? "hidden" : "";
   }, [editorExpanded]);

   const renderEditor = (
      <CxfMonacoEditor
         defaultLanguage="json"
         value={stringifyWithSpaces(contextToDisplay)}
         onMount={handleEditorDidMount}
         options={{
            readOnly: true,
            domReadOnly: true,
            scrollbar: { verticalScrollbarSize: 8 },
            minimap: { enabled: false }
         }}
         height="100%"
         width="100%"
      />
   );

   return (
      <CustomDrawerPanel
         className="context-panel no-translate"
         open={showContextPanel}
         title={t("buttons.context")}
         icon={<ThemedCurlyBracesIcon />}
         actions={<ContextHeaderActions editorRef={editorRef} />}
         isFullWidthActions
         isContext
      >
         {editorExpanded ? (
            createPortal(
               <SoftBox
                  sx={{
                     position: "fixed",
                     inset: 0,
                     width: "100vw",
                     height: "100vh",
                     zIndex: 99999,
                     backgroundColor: isDark ? THEME_COLORS.DARK.PRIMARY_BG : THEME_COLORS.LIGHT.PRIMARY_BG,
                     display: "flex",
                     flexDirection: "column",
                     overflow: "auto"
                  }}
               >
                  <ContextHeaderActions editorRef={editorRef} isFullscreen handleClose={() => setEditorExpanded(false)} />
                  <SoftBox sx={{ flex: 1, position: "relative" }}>
                     {renderEditor}
                     <SoftBox
                        sx={{
                           position: "absolute",
                           bottom: 16,
                           right: 16,
                           zIndex: 100000
                        }}
                     >
                        <EditorExpansion isExpanded={editorExpanded} handleExpansion={() => setEditorExpanded(false)} />
                     </SoftBox>
                  </SoftBox>
               </SoftBox>,
               document.body
            )
         ) : (
            <SoftBox sx={{ height: "100%", position: "relative" }}>
               {renderEditor}
               <EditorExpansion isExpanded={editorExpanded} handleExpansion={() => setEditorExpanded(true)} />
            </SoftBox>
         )}
      </CustomDrawerPanel>
   );
};

export default memo(ContextPanel);
